import React from 'react';
import { motion } from 'framer-motion';
import { Check, AlertCircle, Clock, Users, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export function LiveFloorPreview() {
  return <section className="py-32 bg-[#050505] relative z-10 border-y border-white/5 overflow-hidden" data-id="element-519">
      <div className="max-w-7xl mx-auto px-6" data-id="element-520">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeUp} className="text-center mb-20" data-id="element-521">
          <h2 className="text-4xl md:text-6xl font-display mb-6 leading-[1.0] tracking-tight text-balance text-white" data-id="element-522">
            See your floor. <br className="hidden md:block" data-id="element-523" />
            <span className="text-gray-500" data-id="element-524">In real time.</span>
          </h2>
          <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-525">
            A live diary that thinks ahead. Real-time activity, AI-driven
            seating optimisation, and predictive alerts that stop bottlenecks
            before they happen.
          </p>
        </motion.div>

        {/* Interactive Mockup */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="relative max-w-5xl mx-auto" data-id="element-526">
          {/* Ambient Glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-527" />

          <div className="bg-[#0A0A0A]/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl relative z-10 overflow-hidden flex flex-col h-[600px]" data-id="element-528">
            {/* Timeline Header */}
            <div className="h-16 border-b border-white/5 flex items-center px-6 relative bg-[#111]/50" data-id="element-529">
              <div className="flex items-center gap-12 text-xs font-medium text-gray-500 w-full overflow-hidden" data-id="element-530">
                <span data-id="element-531">5:00 PM</span>
                <span data-id="element-532">5:30 PM</span>
                <span data-id="element-533">6:00 PM</span>
                <span className="text-white relative" data-id="element-534">
                  6:30 PM
                  <motion.div className="absolute -top-6 left-1/2 -translate-x-1/2 w-px h-16 bg-brand-orange" animate={{
                  opacity: [0.5, 1, 0.5]
                }} transition={{
                  duration: 2,
                  repeat: Infinity
                }} data-id="element-535" />
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-brand-orange -ml-1" data-id="element-536" />
                </span>
                <span data-id="element-537">7:00 PM</span>
                <span data-id="element-538">7:30 PM</span>
                <span data-id="element-539">8:00 PM</span>
              </div>
            </div>

            <div className="flex-1 flex flex-col md:flex-row" data-id="element-540">
              {/* Left: Floor Plan */}
              <div className="flex-1 relative p-8 border-b md:border-b-0 md:border-r border-white/5 bg-[#050505]/50 overflow-hidden" data-id="element-541">
                {/* Tables */}
                <motion.div whileHover={{
                scale: 1.05
              }} className="absolute top-12 left-12 w-20 h-20 rounded-full border-2 border-green-500/30 bg-green-500/10 flex items-center justify-center cursor-pointer group" data-id="element-542">
                  <span className="text-green-400 font-medium text-sm" data-id="element-543">T1</span>
                  <div className="absolute -bottom-2 flex gap-1" data-id="element-544">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-545" />
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-546" />
                  </div>
                </motion.div>

                <motion.div whileHover={{
                scale: 1.05
              }} className="absolute top-12 right-24 w-32 h-20 rounded-xl border-2 border-brand-orange/50 bg-brand-orange/10 flex items-center justify-center cursor-pointer" data-id="element-547">
                  <span className="text-brand-orange font-medium text-sm" data-id="element-548">
                    T2
                  </span>
                  <div className="absolute -bottom-2 flex gap-1" data-id="element-549">
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-orange" data-id="element-550" />
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-orange" data-id="element-551" />
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-orange" data-id="element-552" />
                    <div className="w-1.5 h-1.5 rounded-full bg-brand-orange" data-id="element-553" />
                  </div>
                </motion.div>

                <motion.div whileHover={{
                scale: 1.05
              }} className="absolute bottom-24 left-1/2 -translate-x-1/2 w-40 h-24 rounded-xl border-2 border-red-500/30 bg-red-500/5 flex items-center justify-center cursor-pointer" data-id="element-554">
                  <span className="text-red-400 font-medium text-sm" data-id="element-555">T3</span>
                  <div className="absolute -top-3 -right-3 w-6 h-6 rounded-full bg-red-500/20 border border-red-500/50 flex items-center justify-center" data-id="element-556">
                    <AlertCircle className="w-3 h-3 text-red-400" data-id="element-557" />
                  </div>
                  <div className="absolute -bottom-2 flex gap-1" data-id="element-558">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-559" />
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-560" />
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-561" />
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-562" />
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-563" />
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400" data-id="element-564" />
                  </div>
                </motion.div>

                <motion.div whileHover={{
                scale: 1.05
              }} className="absolute bottom-12 right-12 w-20 h-20 rounded-full border-2 border-purple-500/30 bg-purple-500/10 flex items-center justify-center cursor-pointer" data-id="element-565">
                  <span className="text-purple-400 font-medium text-sm" data-id="element-566">
                    T4
                  </span>
                  <div className="absolute -top-2 -left-2 bg-purple-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase" data-id="element-567">
                    VIP
                  </div>
                </motion.div>
              </div>

              {/* Right: Activity Feed */}
              <div className="w-full md:w-80 bg-[#111]/30 p-6 flex flex-col gap-4 overflow-y-auto" data-id="element-568">
                <h4 className="text-xs font-bold tracking-[0.2em] text-gray-500 uppercase mb-2" data-id="element-569">
                  Live Activity
                </h4>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.2
              }} className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-570">
                  <div className="flex items-center gap-2 mb-2" data-id="element-571">
                    <Users className="w-3 h-3 text-green-400" data-id="element-572" />
                    <span className="text-xs text-gray-400" data-id="element-573">Just now</span>
                  </div>
                  <p className="text-sm text-gray-200" data-id="element-574">
                    New booking — Table 1, party of 2, 7:30pm
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.4
              }} className="bg-brand-orange/10 border border-brand-orange/30 rounded-xl p-4" data-id="element-575">
                  <div className="flex items-center gap-2 mb-2" data-id="element-576">
                    <Sparkles className="w-3 h-3 text-brand-orange" data-id="element-577" />
                    <span className="text-xs text-brand-orange" data-id="element-578">
                      AI Suggestion
                    </span>
                  </div>
                  <p className="text-sm text-gray-200" data-id="element-579">
                    Move T8 to T10 for better flow. T8 is running 15m over.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.6
              }} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-id="element-580">
                  <div className="flex items-center gap-2 mb-2" data-id="element-581">
                    <AlertCircle className="w-3 h-3 text-red-400" data-id="element-582" />
                    <span className="text-xs text-red-400" data-id="element-583">No-show risk</span>
                  </div>
                  <p className="text-sm text-gray-200" data-id="element-584">
                    Smith party 6pm — auto SMS confirmation sent.
                  </p>
                </motion.div>

                <motion.div initial={{
                opacity: 0,
                x: 20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: 0.8
              }} className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4" data-id="element-585">
                  <div className="flex items-center gap-2 mb-2" data-id="element-586">
                    <Check className="w-3 h-3 text-purple-400" data-id="element-587" />
                    <span className="text-xs text-purple-400" data-id="element-588">VIP Arrival</span>
                  </div>
                  <p className="text-sm text-gray-200" data-id="element-589">
                    VIP arrival in 15 min: Mr. Chen — Table 4 prepared.
                  </p>
                </motion.div>
              </div>
            </div>
          </div>

          {/* Floating Tags */}
          <div className="flex flex-wrap justify-center gap-4 mt-8" data-id="element-590">
            {['Drag-and-drop seating', 'AI optimisation', 'No-show prevention'].map((tag, i) => <div key={i} className="bg-white/5 border border-white/10 px-4 py-2 rounded-full text-xs text-gray-400 font-medium" data-id="element-591">
                {tag}
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}